import React from 'react'
import type { HTMLAttributeReferrerPolicy } from 'react'

import type { IconComponent } from '@fourthwall/components'
import { Icon, Tooltip, useCopyToClipboard, useMediaQueryContext } from '@fourthwall/components'
import { CopyIcon, TickIcon } from '@fourthwall/icons'

import type { CopyButtonAppearance } from './CopyButton.types'
import * as S from './styled'

export type CopyButtonProps = {
  copyText: string
  appearance?: CopyButtonAppearance
  copyLabel?: string
  label?: string
  href?: string
  iconSize?: number
  referrerPolicy?: HTMLAttributeReferrerPolicy
}

export const CopyButton = ({
  appearance = 'primary',
  label,
  copyLabel = 'Copy',
  href,
  copyText,
  iconSize = 12,
  referrerPolicy = '',
}: CopyButtonProps) => {
  const media = useMediaQueryContext()
  const { isCopied, copyToClipboard } = useCopyToClipboard({
    toastMessage: !media.laptop ? 'Link copied to clipboard' : undefined,
  })

  const renderIconButton = (iconComponent: IconComponent) => (
    <S.IconButton $appearance={appearance} type="button" onClick={() => copyToClipboard(copyText)}>
      <Icon component={iconComponent} height={iconSize} />
    </S.IconButton>
  )

  const renderCopyButton = () => {
    const iconButton = renderIconButton(isCopied ? TickIcon : CopyIcon)

    if (media.laptop) {
      return (
        <Tooltip reference={<S.Reference>{iconButton}</S.Reference>}>
          {isCopied ? 'Copied!' : copyLabel}
        </Tooltip>
      )
    }

    return iconButton
  }

  return (
    <S.Container>
      {label && href && (
        <S.Link
          $appearance={appearance}
          href={href}
          referrerPolicy={referrerPolicy}
          target="_blank"
        >
          {label}
        </S.Link>
      )}
      {renderCopyButton()}
    </S.Container>
  )
}
